import type { HelpRouteRule, PageHelpContent } from './helpTypes';
import { hp } from './helpBuilders';
import { ROUTES } from './routes';

/** Guías ya existentes (texto detallado conservado). */
export const helpDashboard: PageHelpContent = {
  title: 'Indicadores (Dashboard)',
  intro:
    'Resumen operativo del negocio: ventas y compras del período, cobranzas y pagos, y estado de cuentas corrientes de clientes y proveedores.',
  sections: [
    {
      id: 'que-hace',
      title: 'Qué hace esta pantalla',
      body:
        'Consolida métricas y gráficos para que puedas comparar períodos y detectar desvíos rápidamente. Los datos se actualizan al cambiar el filtro de período.',
    },
    {
      id: 'pasos',
      title: 'Pasos recomendados',
      bullets: [
        'Elegí el período: mes actual, mes anterior o un mes/año personalizado.',
        'Revisá tarjetas de totales y el gráfico de evolución mensual.',
        'Usá los accesos desde las tarjetas (cuando correspondan) para ir al detalle de facturas con el rango de fechas del período seleccionado.',
      ],
    },
    {
      id: 'campos',
      title: 'Controles importantes',
      bullets: [
        'Filtro de período: define qué mes/año se usa para ventas, compras y derivados.',
        'Indicadores de cuenta corriente: reflejan saldos y vencimientos según la información del servidor.',
      ],
    },
    {
      id: 'errores',
      title: 'Si algo falla',
      bullets: [
        'Si aparece error al cargar, verificá conexión y volvé a intentar; el mensaje suele indicar el motivo devuelto por el servidor.',
        'Si un total no coincide con lo esperado, confirmá que el período seleccionado sea el correcto.',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      bullets: [
        'Compará mes actual vs anterior cambiando el filtro para ver tendencias.',
        'Desde aquí podés saltar a listados filtrados sin rearmar fechas a mano.',
      ],
    },
  ],
  links: [{ label: 'Ir a facturas de venta', to: '/ventas/facturas' }],
};

export const helpFacturaVenta: PageHelpContent = {
  title: 'Factura de venta',
  intro:
    'Emisión y consulta de facturas de venta: cliente, ítems, totales, forma de pago y datos de entrega. Podés crear una factura nueva, cargar desde pedido/cotización o abrir una existente en modo ver o editar.',
  sections: [
    {
      id: 'que-hace',
      title: 'Qué hace esta pantalla',
      body:
        'Centraliza la cabecera del comprobante (cliente, fechas, condiciones) y el detalle de productos o servicios. El sistema calcula importes según precios, descuentos e IVA según corresponda.',
    },
    {
      id: 'pasos',
      title: 'Pasos recomendados',
      bullets: [
        'Seleccioná el cliente; completá datos obligatorios de cabecera (fecha, lista de precios, vendedor, etc. según tu proceso).',
        'Agregá líneas de detalle buscando artículos o cargando desde un pedido/cotización si llegaste con esos parámetros en la URL.',
        'Revisá totales, forma de pago y transporte antes de grabar.',
        'En modo solo lectura (ver) no se modifican datos; en editar ajustá lo necesario y guardá.',
      ],
    },
    {
      id: 'campos',
      title: 'Campos y conceptos clave',
      bullets: [
        'Cliente: determina condición fiscal, precios y datos de facturación.',
        'Forma de pago: debe ser coherente con la condición de venta (contado, cuenta corriente, etc.).',
        'Transporte / ubicación: útiles para logística y documentación.',
        'Detalle: cantidad, precio unitario, descuentos por línea; verificá moneda y cotización si operás en USD.',
      ],
    },
    {
      id: 'errores',
      title: 'Errores frecuentes',
      bullets: [
        'Cliente no seleccionado o incompleto: el sistema puede rechazar el guardado hasta completar datos mínimos.',
        'Artículo sin precio o cantidad inválida: revisá la línea antes de confirmar.',
        'Inconsistencias al traer pedido/cotización: verificá que el documento origen siga vigente y permita facturar.',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      bullets: [
        'Si abrís la pantalla con ?pedidoId= o ?cotizacionId= en la URL, el flujo puede precargar datos desde ese documento.',
        'Desde seguimiento de ventas pendientes podés llegar con estado para aplicar cantidades sugeridas.',
      ],
    },
  ],
  links: [
    { label: 'Listado de facturas', to: '/ventas/facturas' },
    { label: 'Pedidos', to: '/ventas/pedidos' },
    { label: 'Cotizaciones', to: '/ventas/cotizaciones' },
  ],
};

export const helpNotaCreditoCompra: PageHelpContent = {
  title: 'Nueva nota de crédito de compra',
  intro:
    'Registro de una nota de crédito recibida del proveedor. Suele asociarse a una factura de compra origen para mantener coherencia de cantidades, precios e IVA.',
  sections: [
    {
      id: 'que-hace',
      title: 'Qué hace esta pantalla',
      body:
        'Permite cargar cabecera (proveedor, compañía, fechas, comprobante), ítems de producto y conceptos/gastos, impuestos adicionales y aplicar datos desde una factura de compra o compras pendientes.',
    },
    {
      id: 'pasos',
      title: 'Pasos recomendados',
      bullets: [
        'Si tenés la factura origen, usá “Buscar factura” o ingresá el número de formulario para traer proveedor, ítems y descuentos.',
        'Verificá compañía fiscal (ID compañía) y letra/tipo de comprobante.',
        'Ajustá cantidades o importes según lo acordado con el proveedor en la nota de crédito.',
        'Revisá separación entre ítems de producto (tipo C) y conceptos/gastos (tipo G) antes de guardar.',
        'Grabá cuando totales e IVA reflejen el comprobante físico/digital.',
      ],
    },
    {
      id: 'campos',
      title: 'Campos importantes',
      bullets: [
        'Factura origen: al seleccionarla se precargan proveedor, ubicación, forma de pago y líneas.',
        'Proveedor y ubicación de destino: impactan stock y contabilización según configuración.',
        'Bonificaciones por cabecera y por ítem: influyen en costos e impuestos.',
        'Impuestos adicionales: agregá solo los que correspondan al comprobante real.',
      ],
    },
    {
      id: 'errores',
      title: 'Errores frecuentes',
      bullets: [
        'Número de factura origen inválido: debe existir en el sistema y ser del proveedor correcto.',
        'Mezclar mal productos y conceptos: revisá el tipo de línea (C vs G) frente al comprobante.',
        'Fechas de emisión / aplicación IVA incoherentes con la factura: pueden generar diferencias en reportes.',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      bullets: [
        'Si venís desde “compras pendientes”, el estado de navegación puede precargar líneas vinculadas.',
        'Expandí acordeones de productos/conceptos para revisar todo antes de confirmar.',
      ],
    },
  ],
  links: [
    { label: 'Listado de notas de crédito (compras)', to: '/compras/notas-credito' },
    { label: 'Facturas de compra', to: '/compras/facturas' },
  ],
};

const helpMenuPrincipal = hp(
  'Menú principal',
  'Punto de entrada para elegir el módulo (Ventas, Compras, Inventario, Tesorería e Indicadores). Cada tarjeta lleva al submenú correspondiente.',
  {
    pasos: [
      'Revisá la descripción de cada módulo para ubicar la funcionalidad que necesitás.',
      'Hacé clic en la tarjeta del módulo para ver todas las opciones disponibles.',
      'Usá los chips de acceso rápido cuando quieras ir directo a un submenú sin pasar por la pantalla intermedia.',
    ],
    campos: [
      'Las tarjetas reflejan la configuración del menú lateral: si falta algo, puede deberse a permisos de usuario.',
    ],
    errores: ['Si no ves un módulo esperado, consultá permisos con el administrador.'],
    tips: ['Podés volver siempre aquí desde el logo o enlaces “Volver al menú principal”.'],
    links: [{ label: 'Indicadores', to: '/dashboard' }],
  }
);

const helpModuloVentas = hp(
  'Módulo Ventas',
  'Selector de funciones de ventas: cotizaciones, pedidos, facturas, remitos, clientes, informes y más.',
  {
    pasos: [
      'Elegí la tarjeta del proceso que necesitás (por ejemplo Facturas o Pedidos).',
      'Usá “Volver al menú principal” si querés cambiar de módulo.',
    ],
    tips: ['El menú lateral repite estas mismas opciones para acceso rápido.'],
    links: [
      { label: 'Facturas', to: '/ventas/facturas' },
      { label: 'Clientes', to: '/ventas/clientes' },
    ],
  }
);

const helpModuloCompras = hp(
  'Módulo Compras',
  'Selector de funciones de compras: órdenes, remitos, facturas, proveedores, notas de crédito e informes.',
  {
    pasos: [
      'Seleccioná la operación (facturas de compra, órdenes, informes de cuentas a pagar, etc.).',
      'Desde aquí podés saltar a altas o listados según la tarjeta.',
    ],
    links: [
      { label: 'Facturas de compra', to: '/compras/facturas' },
      { label: 'Proveedores', to: '/compras/proveedores' },
    ],
  }
);

const helpModuloInventario = hp(
  'Módulo Inventario',
  'Selector de stock, productos maestros, ajustes/transferencias e informes de inventario.',
  {
    pasos: [
      'Elegí Productos para el maestro de artículos o Transferencias para movimientos entre ubicaciones.',
      'Entrá a Informes para stock por depósito y movimientos.',
    ],
    links: [
      { label: 'Productos', to: '/inventario/productos' },
      { label: 'Transferencias', to: '/inventario/transferencias' },
    ],
  }
);

const helpModuloTesoreria = hp(
  'Módulo Tesorería',
  'Selector de cobranzas, pagos, caja, bancos, conciliación e informes de tesorería.',
  {
    pasos: [
      'Elegí Recibos, Orden de pago, Movimientos de caja u otra opción según la tarea.',
      'Las tablas auxiliares (bancos, carteras, medios de cobro/pago) suelen estar en la misma sección.',
    ],
    links: [
      { label: 'Recibos', to: '/tesoreria/recibos' },
      { label: 'Planilla de caja', to: '/tesoreria/movimientos-caja' },
    ],
  }
);

const helpConsultaIa = (modulo: string): PageHelpContent =>
  hp(`Consulta IA (${modulo})`, `Asistente de consultas en lenguaje natural sobre datos del módulo ${modulo}.`, {
    pasos: [
      'Formulá preguntas concretas sobre ventas, saldos o listados permitidos por tu perfil.',
      'Revisá siempre la respuesta contra reportes oficiales para decisiones críticas.',
    ],
    errores: [
      'Si la IA no tiene acceso a un dato, puede deberse a permisos o a que la información no esté cargada.',
    ],
    tips: ['Usá fechas y filtros en la pregunta para acotar resultados.'],
  });

/** Lista / formulario en subrutas no cubiertas por una guía más específica. */
const helpListadoTrabajoGenerico = hp(
  'Listados y operaciones',
  'Pantalla de trabajo con listados, filtros y acciones sobre registros (altas, edición y consulta según permisos).',
  {
    pasos: [
      'Usá filtros por fecha, estado o entidad para acotar resultados.',
      'Abrí una fila para ver detalle o editar cuando esté disponible.',
      'Para crear un registro nuevo buscá el botón “Nuevo” u opción equivalente.',
    ],
    campos: [
      'Las columnas dependen del tipo de documento o maestro: números, fechas, importes, referencias.',
    ],
    errores: ['Si no cargan datos, revisá sesión, permisos de perfil y filtros demasiado restrictivos.'],
  }
);

const helpMaestroCliente = hp(
  'Clientes',
  'Alta, edición y consulta de clientes: datos fiscales, condiciones de venta, direcciones y parámetros comerciales.',
  {
    pasos: [
      'En listado: buscá por nombre, código o CUIT.',
      'En alta/edición: completá obligatorios marcados o validados al guardar.',
      'Grabá antes de cambiar de pantalla.',
    ],
    campos: [
      'Condición de venta y lista de precios suelen definir comportamiento en facturación.',
      'CUIT/DNI y categoría fiscal deben coincidir con documentación oficial.',
    ],
    errores: ['Duplicidad de CUIT o código: el sistema puede rechazar el guardado.'],
    links: [{ label: 'Facturas', to: '/ventas/facturas' }],
  }
);

const helpPedidosCotizaciones = (tipo: string): PageHelpContent =>
  hp(tipo, `Gestión del circuito comercial de ${tipo.toLowerCase()}: cabecera, ítems, precios y estados hasta facturar o cerrar.`, {
    pasos: [
      'Seleccioná cliente y condiciones comerciales.',
      'Agregá líneas de productos con cantidades y precios.',
      'Cuando corresponda, generá factura o pedido siguiente desde las acciones disponibles.',
    ],
    campos: ['Estados del documento y fechas impactan la disponibilidad para facturar.'],
    tips: ['Podés llegar desde URLs con ID para editar o solo ver según la ruta.'],
  });

const helpFacturasListadoVentas = hp(
  'Facturas de venta (listado)',
  'Consulta del listado de facturas emitidas con filtros y acceso al detalle o navegación al comprobante.',
  {
    pasos: [
      'Filtrá por fechas y cliente cuando exista.',
      'Abrí una fila para ver detalle o continuar edición si está permitido.',
      'Para emitir una nueva factura usá “Nuevo” o la acción equivalente.',
    ],
    links: [{ label: 'Nueva factura', to: ROUTES.ventas.facturas.pos }],
  }
);

const helpRemitosVenta = hp(
  'Remitos de venta',
  'Gestión de remitos de entrega vinculados al circuito de ventas y logística.',
  {
    pasos: [
      'Creá o buscá remitos según política de la empresa.',
      'Asociá cliente, transporte y líneas cuando la pantalla lo permita.',
    ],
    tips: ['Verificá depósito y ubicación si el flujo involucra stock.'],
  }
);

const helpNotasCreditoVenta = hp(
  'Notas de crédito de venta',
  'Emisión y seguimiento de notas de crédito sobre facturas de venta.',
  {
    pasos: [
      'Indicá factura origen o cliente según corresponda.',
      'Revisá importes, alícuotas y letra antes de confirmar.',
    ],
    errores: ['Montos que excedan lo facturable pueden ser rechazados por validaciones.'],
  }
);

const helpVentasInformesHub = hp(
  'Informes de ventas',
  'Centro de acceso a informes comerciales: resultados de ventas, seguimiento, cuentas por cobrar, IVA ventas y resúmenes de cuenta.',
  {
    pasos: [
      'Elegí el informe según lo que necesites analizar.',
      'Completá parámetros de fechas y filtros antes de ejecutar.',
    ],
    tips: ['Muchos informes permiten exportar o imprimir desde la barra de herramientas.'],
  }
);

const helpInformesPersonalizados = hp(
  'Mis informes',
  'Reportes de la biblioteca instalados por la empresa. El administrador puede instalarlos o quitarlos desde el store.',
  {
    pasos: [
      'Ejecutá un informe instalado e indicá los parámetros si los pide.',
      'Si sos administrador, abrí el store para instalar o quitar informes de la empresa.',
    ],
  }
);

const helpInformeSeguimientoVentas = hp(
  'Seguimiento de ventas',
  'Seguimiento de pedidos/cotizaciones/facturas en curso para priorizar entregas y cobranzas.',
  {
    pasos: ['Filtrá por vendedor, cliente o fecha.', 'Usá las acciones por línea para avanzar el proceso.'],
    tips: ['Ideal para vista operativa diaria del equipo comercial.'],
  }
);

const helpInformeCuentasCobrar = hp(
  'Cuentas por cobrar',
  'Análisis de deudas de clientes y vencimientos para cobranzas.',
  {
    pasos: ['Definí fechas y filtros de cliente o vendedor.', 'Exportá si necesitás trabajo en Excel.'],
    errores: ['Si los saldos no coinciden con cuenta corriente, revisá comprobantes pendientes de sincronizar.'],
  }
);

const helpInformeResumenCuentaVentas = hp(
  'Resumen de cuenta (cliente)',
  'Estado de cuenta y movimientos de un cliente seleccionado.',
  {
    pasos: ['Seleccioná cliente y rango de fechas.', 'Revisá cobranzas aplicadas y saldos.'],
    links: [{ label: 'Clientes', to: '/ventas/clientes' }],
  }
);

const helpInformeLibroIvaVentas = hp(
  'Libro IVA ventas',
  'Informe fiscal de ventas para presentaciones y auditoría.',
  {
    pasos: ['Validá período fiscal y punto de venta si aplica.', 'Exportá según formato requerido por tu estudio contable.'],
    errores: ['Comprobantes mal clasificados pueden figurar en incorrecto régimen de IVA.'],
  }
);

const helpInformeVentasDinamico = hp(
  'Informe de ventas dinámico',
  'Consulta dinámica de ventas con agrupaciones y filtros extendidos.',
  {
    pasos: ['Seleccioná dimensiones de análisis (cliente, período, artículo).', 'Guardá vistas útiles como referencia interna.'],
  }
);

const helpInformeVentasPorProducto = hp(
  'Informe de ventas por producto',
  'Resumen agrupado por producto, tipo de artículo, ramo, línea y clase (NombreRamo, NombreLinea, NombreClase en BP_RPT_Ventas01), con totales y detalle de líneas. En filtros avanzados podés discriminar comprobantes categorizados y no categorizados.',
  {
    pasos: [
      'Definí fechas y filtros opcionales por sucursal, tipo, ramo, línea y clase.',
      'En filtros avanzados elegí Todos, Categorizado o No Categorizado para ver el volumen real por línea o ramo.',
      'Generá el informe y exportá a CSV o Excel o imprimí la tabla.',
      'Usá Ver Detalle para ver las líneas que componen cada grupo.',
    ],
  }
);

const helpInformeVentasResultados = hp(
  'Informe de resultados de ventas',
  'Listado de ventas del período por comprobante (mostrador y resto), con vendedor, forma de pago y detalle de artículos.',
  {
    pasos: [
      'Definí fechas y, si querés, filtrá por cliente, sucursal u ubicación.',
      'Generá el informe y expandí cada comprobante para ver los artículos.',
      'Exportá a CSV, Excel o PDF según necesites.',
    ],
    tips: ['Ideal para seguir las ventas del día o del período en el mostrador.'],
  }
);

const helpVentasMaestrosAux = hp(
  'Maestros de ventas',
  'Administración de listas de precios, vendedores, rutas, visitas y tablas auxiliares del circuito comercial.',
  {
    pasos: ['Editá registros desde la grilla o formulario.', 'Verificá impacto en precios y comisiones al cambiar datos.'],
    tips: ['Los cambios en listas de precios pueden afectar cotizaciones futuras.'],
  }
);

const helpArticuloListaPrecio = hp(
  'Artículos por lista de precio',
  'Relación entre artículos y listas de precios: vigencia, precios y condiciones.',
  {
    pasos: ['Seleccioná lista y artículo.', 'Ingresá precios y guardá cambios por ítem o lote si la pantalla lo permite.'],
    errores: ['Precio cero o sin moneda pueden invalidar cotizaciones.'],
  }
);

const helpComprasFacturas = hp(
  'Facturas de compra',
  'Registro y consulta de facturas de proveedor: ítems, impuestos, imputaciones y vínculo con órdenes y remitos.',
  {
    pasos: [
      'Desde listado: filtrá y abrí un comprobante para verificar o auditar.',
      'Desde nuevo: cargá proveedor, compañía fiscal, ítems y totales según factura física.',
    ],
    campos: ['Tipo de ítem producto vs gasto/concepto según configuración.', 'Percepciones y retenciones si aplican.'],
    tips: ['Conciliar contra orden de compra o remito cuando exista flujo formal.'],
    links: [{ label: 'Órdenes de compra', to: '/compras/ordenes' }],
  }
);

const helpComprasOrdenes = hp(
  'Órdenes de compra',
  'Creación y seguimiento de órdenes a proveedores antes de facturar o recibir mercadería.',
  {
    pasos: ['Definí proveedor, condiciones y líneas solicitadas.', 'Actualizá estado cuando recibas mercadería o factura.'],
    errores: ['Cantidades toleradas pueden estar limitadas por política de compras.'],
  }
);

const helpComprasRemitos = hp(
  'Remitos de compra',
  'Recepciones de mercadería contra órdenes o facturas.',
  {
    pasos: ['Registrá cantidades recibidas por línea.', 'Verificá depósito destino para stock.'],
    tips: ['Útil como paso previo a la factura de compra en muchos procesos.'],
  }
);

const helpProveedores = hp(
  'Proveedores',
  'Maestro de proveedores: datos impositivos, condiciones de compra y categorización.',
  {
    pasos: [
      'Buscá antes de dar de alta para evitar duplicados.',
      'Completá CUIT y categoría fiscal con precisión.',
      'En Extras, cargá la Nota 1 (uso interno) con observaciones operativas (cheques, flete/sobres, plazos comerciales, etc.); se usa luego en la impresión de OP tipo P.',
    ],
    links: [{ label: 'Facturas de compra', to: '/compras/facturas' }],
  }
);

const helpRamosProv = hp(
  'Ramos de proveedores',
  'Clasificación de proveedores por ramo para informes y políticas comerciales.',
  {
    pasos: ['Mantené códigos claros y sin duplicar descripciones.'],
  }
);

const helpNotasCreditoCompraLista = hp(
  'Notas de crédito de compra',
  'Listado y consulta de notas de crédito recibidas; acceso al detalle por número de formulario.',
  {
    pasos: ['Filtrá por fechas o proveedor.', 'Para nueva nota usá la opción correspondiente desde el listado.'],
    links: [
      { label: 'Nueva nota de crédito', to: '/compras/notas-credito/nuevo' },
      { label: 'Facturas de compra', to: '/compras/facturas' },
    ],
  }
);

const helpComprasInformesHub = hp(
  'Informes de compras',
  'Informes de cuentas a pagar, libro IVA compras, resumen de cuenta proveedor y seguimiento.',
  {
    pasos: ['Elegí informe y definí período.', 'Validá totales contra facturas registradas.'],
  }
);

const helpInformeComprasDinamico = hp(
  'Informe de compras dinámico',
  'Consulta dinámica de compras con agrupaciones y filtros extendidos.',
  {
    pasos: ['Seleccioná dimensiones de análisis (proveedor, período, artículo).', 'Guardá vistas útiles como referencia interna.'],
  }
);

const helpSeguimientoCompras = hp(
  'Seguimiento de compras',
  'Seguimiento operativo del circuito de compras pendientes.',
  {
    pasos: ['Priorizá por fecha prometida o proveedor.', 'Coordiná con depósito y recepción.'],
  }
);

const helpInventarioProductos = hp(
  'Productos',
  'Maestro de artículos: códigos, descripciones, unidades, precios base y datos de stock.',
  {
    pasos: ['Buscá por código o descripción antes de crear uno nuevo.', 'Completá unidad de medida y tipo de ítem.'],
    errores: ['Código duplicado: el sistema suele impedir guardar.'],
    links: [{ label: 'Transferencias', to: '/inventario/transferencias' }],
  }
);

const helpInventarioTransferencias = hp(
  'Ajustes y transferencias',
  'Movimientos de stock entre ubicaciones o ajustes por diferencias.',
  {
    pasos: ['Indicá depósito origen y destino.', 'Detallá líneas con cantidades y motivo si aplica.'],
    tips: ['Verificá lotes o series si el artículo lo requiere.'],
  }
);

const helpInventarioClasificacion = hp(
  'Clasificación de productos',
  'Ramos, líneas y clases para organizar el catálogo y reportes.',
  {
    pasos: ['Asigná códigos únicos y jerarquías coherentes.', 'Evitá borrar si hay productos asociados.'],
  }
);

const helpUnidadesMedida = hp(
  'Unidades de medida',
  'Definición de unidades y conversiones usadas en compras, ventas y stock.',
  {
    pasos: ['Creá unidades claras (UN, KG, M2, etc.).', 'Relacioná con productos desde el maestro de artículos.'],
  }
);

const helpDescuentos = hp(
  'Descuentos',
  'Maestro de reglas de descuento comercial. Cada registro puede aplicar a un cliente, una ruta, una lista, un artículo u otra dimensión; no hace falta completar todas.',
  {
    pasos: [
      'Completá solo las dimensiones a las que aplica la regla.',
      'Indicá el porcentaje y, si corresponde, la fecha de caducidad (válido todo ese día; al siguiente ya no aplica).',
      'El backend decide a quién aplica según unidad de medida, cantidades y precios.',
    ],
    errores: ['Si el servidor rechaza la combinación, el mensaje indica el motivo; no se valida en pantalla.'],
  }
);

const helpInventarioOperativo = hp(
  'Operaciones de inventario',
  'Devoluciones, producción, picking o conteo físico según la pantalla.',
  {
    pasos: ['Seguí el flujo indicado en cada paso de la pantalla.', 'Confirmá ubicaciones y cantidades relevadas.'],
    errores: ['Diferencias grandes pueden requerir ajuste supervisor.'],
  }
);

const helpInventarioInformes = hp(
  'Informes de inventario',
  'Stock por depósito, movimientos y stock valorizado a fecha.',
  {
    pasos: [
      'Definí depósito y fechas según el informe.',
      'En stock valorizado, elegí la fecha de corte y, si querés, un depósito. Sin depósito se totaliza en todos. El valorizado usa el costo neto actual.',
    ],
  }
);

const helpTesoreriaRecibos = hp(
  'Recibos de cobranza',
  'Registro de cobranzas a clientes: medios de pago, imputación a facturas y valores recibidos.',
  {
    pasos: ['Seleccioná cliente y comprobantes a cancelar o imputar.', 'Distribuí importes entre medios de cobro.', 'Grabá y verificá numeración.'],
    campos: ['Medios MCP, cheques y efectivo según parametrización.', 'Caja o usuario responsable si aplica.'],
    tips: ['Conciliá con extracto bancario cuando el cobro sea transferencia.'],
    links: [{ label: 'Facturas venta', to: '/ventas/facturas' }],
  }
);

const helpTesoreriaOrdenPago = hp(
  'Órdenes de pago',
  'Instrucciones de pago a proveedores: selección de facturas, retenciones y medios.',
  {
    pasos: ['Elegí proveedor y documentos a pagar.', 'Definí forma de pago y fecha valor.', 'Generá la orden y seguí estado hasta ejecutar.'],
    errores: ['Importe mayor al saldo puede estar bloqueado por validaciones.'],
    links: [{ label: 'Facturas compra', to: '/compras/facturas' }],
  }
);

const helpTesoreriaMovVarios = hp(
  'Movimientos varios de caja',
  'Ingresos y egresos no ligados a un recibo u orden estándar, según política.',
  {
    pasos: ['Indicá concepto imputable y caja.', 'Verificá signo del importe (entrada/salida).'],
  }
);

const helpTesoreriaMovVariosNuevo = hp(
  'Nuevo movimiento varios',
  'Registro de ingresos o egresos de caja con concepto imputable.',
  {
    pasos: [
      'Seleccioná ingreso o egreso según el movimiento.',
      'Indicá el concepto imputable y la sucursal.',
      'Elegí el grupo de caja y agregá al menos un medio de pago.',
      'Verificá el signo del importe (entrada/salida) antes de registrar.',
    ],
  }
);

const helpTesoreriaMovBancarios = hp(
  'Movimientos bancarios',
  'Registro de movimientos en cuentas bancarias y conciliación parcial.',
  {
    pasos: ['Seleccioná banco y cuenta.', 'Cargá fecha, importe y referencia.'],
    tips: ['Útil junto con conciliación bancaria formal.'],
  }
);

const helpConciliacionBancaria = hp(
  'Conciliación bancaria',
  'Emparejamiento de extracto banco vs movimientos internos.',
  {
    pasos: ['Importá o cargá movimientos del extracto.', 'Marcá coincidencias y diferencias pendientes.'],
    errores: ['Diferencias pueden deberse a cheques no acreditados o errores de fecha valor.'],
  }
);

const helpTablasBancarias = hp(
  'Bancos y tablas de tesorería',
  'Maestros de bancos, carteras, grupos MCP, medios MCP y marcas de tarjeta.',
  {
    pasos: ['Altas desde “Nuevo”.', 'Editá códigos existentes desde la lista.', 'Coordiná con administración antes de eliminar o desactivar.'],
    errores: ['Referencias en movimientos pueden impedir borrar registros.'],
  }
);

const helpPlanillaCaja = hp(
  'Planilla de caja',
  'Vista operativa de movimientos de caja y posiciones por usuario/fecha.',
  {
    pasos: ['Filtrá por fecha y sucursal o usuario si existe.', 'Revisá ingresos y egresos del día.'],
    tips: ['Útil como cierre diario de caja.'],
  }
);

const helpDisponibilidades = hp(
  'Disponibilidades',
  'Saldo disponible por cuenta bancaria o caja para decidir pagos.',
  {
    pasos: ['Seleccioná entidad financiera.', 'Interpretá saldos contables vs extracto.'],
  }
);

const helpTesoreriaTransferencias = hp(
  'Traspasos (tesorería)',
  'Traspasos entre cuentas propias o instrumentadas desde tesorería.',
  {
    pasos: ['Definí origen, destino e importe.', 'Verificá fecha valor.'],
  }
);

const helpTesoreriaInformesHub = hp(
  'Informes de tesorería',
  'Cheques de terceros y propios, movimientos de carteras y reportes relacionados.',
  {
    pasos: ['Elegí el tipo de informe.', 'Aplicá filtros de fecha y cartera.'],
  }
);

const helpInformeCheques = hp(
  'Informes de cheques',
  'Seguimiento de cheques recibidos, propios y estado de acreditación.',
  {
    pasos: ['Filtrá por estado y fechas.', 'Detectá cheques rechazados o pendientes.'],
  }
);

const helpMovimientosCarterasInforme = hp(
  'Movimientos de carteras',
  'Detalle de movimientos por cartera de valores o cheques.',
  {
    pasos: ['Seleccioná cartera y período.', 'Exportá si necesitás trabajo externo.'],
  }
);

const helpSistemaUsuarios = hp(
  'Usuarios del tenant',
  'Administración de usuarios vinculados al tenant en el que estás trabajando: alta, roles y accesos dentro de esa empresa. No administra identidades globales ni asignaciones a otros tenants (eso corresponde a Usuarios maestros, solo SUPERADMIN).',
  {
    pasos: [
      'Creá el usuario con identificador único dentro del tenant.',
      'Asigná el rol según la política de seguridad de la empresa.',
      'Configurá ubicaciones y grupos de caja en las pestañas del formulario si aplica.',
      'Forzá cambio de contraseña cuando corresponda.',
    ],
    errores: [
      'Usuario duplicado o contraseña que no cumple política serán rechazados.',
      'Solo verás y gestionarás usuarios del tenant actual; si falta alguien, puede no estar asignado a este tenant.',
    ],
    tips: [
      'No compartas credenciales; usá usuario personal.',
      'Para crear la identidad global y vincularla a varios tenants, usá Usuarios maestros.',
    ],
  }
);

const helpSistemaTipoFormulario = hp(
  'Tipos de formulario',
  'Parametrización de tipos de comprobantes y secuencias asociadas.',
  {
    pasos: ['Revisá puntos de venta, letras y numeración.', 'Impacta en todos los circuitos que usan ese tipo.'],
    errores: ['Cambios en uso pueden afectar comprobantes futuros; coordiná con administración.'],
  }
);

const helpSistemaGeografia = hp(
  'Provincias y localidades',
  'Tablas geográficas para direcciones y datos fiscales.',
  {
    pasos: ['Mantené códigos alineados con estándares que use la empresa.', 'Evitá duplicar localidades.'],
  }
);

const helpSistemaMonedas = hp(
  'Cotizaciones de moneda',
  'Carga y mantenimiento de cotizaciones para operaciones en moneda extranjera.',
  {
    pasos: ['Registrá fecha y cotización oficial o interna.', 'Verificá vigencia para facturas del día.'],
    tips: ['Las facturas en USD suelen tomar la última cotización cargada.'],
  }
);

const helpSistemaTiposMoneda = hp(
  'Monedas',
  'Alta y mantenimiento de tipos de moneda (código, descripción, símbolo y datos FCE).',
  {
    pasos: ['Definí un código único entre 1 y 255.', 'Completá descripción y símbolo para listas y precios.'],
    tips: ['Las cotizaciones por fecha se gestionan en Cotizaciones de moneda.'],
  }
);

const helpSistemaCompanias = hp(
  'Compañías (fiscal)',
  'Datos de sociedades o unidades de negocio para facturación y reportes.',
  {
    pasos: ['Completá CUIT y condición IVA.', 'Relacioná con puntos de venta y tipos de formulario.'],
  }
);

const helpSistemaCajaUsuario = hp(
  'Caja por usuario',
  'Asignación de cajas y grupos MCP a usuarios para operar en tesorería.',
  {
    pasos: ['Asociá usuario con grupo o caja permitida.', 'Verificá restricciones por sucursal.'],
  }
);

const helpConfigTransportes = hp(
  'Transportes',
  'Maestro de transportes/expresos para remitos y logística.',
  {
    pasos: ['Alta y edición de transportistas.', 'Usá códigos consistentes con documentación.'],
  }
);

const helpConfigImpuestos = hp(
  'Impuestos',
  'Definición de impuestos, tasas y reglas de cálculo.',
  {
    pasos: ['Revisá alícuotas vigentes.', 'Coordiná cambios con compras y ventas.'],
    errores: ['Errores de tasa impactan IVA y percepciones.'],
  }
);

const helpConfigUbicaciones = hp(
  'Ubicaciones / depósitos',
  'Depósitos físicos y lógicos para stock y movimientos.',
  {
    pasos: ['Definí códigos de depósito claros.', 'Marcá cuáles intervienen en venta o compra.'],
  }
);

const helpConfigSucursales = hp(
  'Sucursales de venta',
  'Parametrización de puntos de venta y series asociadas.',
  {
    pasos: ['Relacioná sucursal con tipos de formulario y numeración.', 'Verificá permisos por usuario.'],
  }
);

const helpConfigParametros = hp(
  'Parámetros del sistema',
  'Valores de configuración de la empresa. Solo se pueden editar valor y comentario; el alta se realiza desde el backend.',
  {
    pasos: ['Buscá por sección o texto libre.', 'Editá el valor según la documentación del parámetro.'],
    errores: ['Cambios incorrectos pueden afectar ventas, tesorería o cálculos fiscales.'],
  }
);

const helpConfigTokens = hp(
  'Tokens de integración',
  'Administración de accesos para integraciones externas (APIs, servicios).',
  {
    pasos: ['Creá tokens con alcance mínimo necesario.', 'Revocá tokens comprometidos de inmediato.'],
    errores: ['Exponer un token equivale a dar acceso a datos; manejalo como contraseña.'],
  }
);

const helpConfigClavesAgente = hp(
  'Claves de agente (LLM)',
  'Claves personales para conectar OpenClaw, Claude Cowork, DeepSeek u otros agentes al ERP vía MCP/REST.',
  {
    queHace:
      'Cada clave actúa como tu usuario: solo ve los tenants y permisos que tenés. El Bearer se muestra una sola vez; podés revocarlo en cualquier momento.',
    pasos: [
      'Creá una clave con nombre y vencimiento (máx. 90 días).',
      'Copiá el Bearer y el snippet MCP; guardalos en variables de entorno.',
      'Configurá el agente con la URL /mcp y Authorization Bearer.',
      'Si necesitás otra empresa, usá el header X-Tenant-Id con un tenant al que tengas acceso.',
    ],
    errores: [
      'Si el agente recibe 403, faltan permisos de negocio o el módulo CRM no está habilitado.',
      'Una clave revocada o vencida responde 401.',
    ],
    tips: ['No compartas la clave: es tan sensible como tu contraseña.'],
  }
);

const helpConfigWebhooks = hp(
  'Webhooks',
  'Notificaciones HTTP que eOne envía a tu sistema cuando ocurren eventos de negocio (HMAC, reintentos y logs).',
  {
    queHace:
      'Permite crear suscripciones outbound: URL de destino, eventos, secret HMAC, filtros, reintentos, timeout, headers y alerta por mail. Desde el listado podés probar, ver logs y reintentar entregas fallidas.',
    pasos: [
      'Creá un webhook con URL HTTPS y al menos un evento.',
      'Si tu sistema debe validar autenticidad, cargá un secret HMAC.',
      'Usá Probar para enviar un POST de prueba y revisá logs en el detalle.',
      'Si un envío queda failed, reintentalo desde el detalle.',
    ],
    campos: [
      'URL: endpoint que recibe POST JSON.',
      'Secret: firma HMAC-SHA256 del body crudo (header X-Webhook-Signature).',
      'Filtros: reglas AND/OR para no disparar la URL si el documento no cumple.',
      'Máx. reintentos y timeout: controlan la entrega automática.',
    ],
    errores: [
      'Sin URL o sin eventos no se guarda.',
      'Si la firma no valida en tu lado, verificá el body crudo (no el JSON re-serializado).',
      'HTTP distinto de 2xx o timeout genera reintentos y, al agotarlos, estado failed.',
    ],
    tips: [
      'Respondé 2xx apenas aceptás el evento y procesá en segundo plano.',
      'La documentación técnica completa está en esta misma sección: Documentación.',
    ],
    links: [{ label: 'Documentación técnica de webhooks', to: '/configuraciones/webhooks/ayuda' }],
  }
);

const helpConfigWebhooksDocs = hp(
  'Documentación de webhooks',
  'Guía técnica para integrar notificaciones HTTP outbound de eOne: payload, HMAC-SHA256, eventos, reintentos y ejemplos.',
  {
    queHace:
      'Explica cómo recibir, verificar y responder los POST que eOne envía a tu URL, con el mismo alcance que la documentación del API.',
    pasos: [
      'Leé Introducción y el flujo general para ubicar el modelo (eOne llama a tu sistema).',
      'Configurá la suscripción en Webhooks y, si aplica, el secret HMAC.',
      'Implementá la verificación de X-Webhook-Signature sobre el body crudo.',
      'Respondé HTTP 2xx y usá Probar / logs para validar la entrega.',
    ],
    campos: [
      'Firma: HMAC-SHA256 hex con prefijo sha256=.',
      'Éxito de entrega: cualquier HTTP 2xx.',
      'Backoff exponencial hasta maxReintentos (default 3).',
    ],
    tips: ['Volvé a la pantalla de Webhooks para crear o probar una suscripción.'],
    links: [{ label: 'Configuración de webhooks', to: '/configuraciones/webhooks' }],
  }
);

const helpContabilidadImputables = hp(
  'Cuentas imputables',
  'Plan de cuentas imputables para movimientos contables detallados.',
  {
    pasos: ['Mantené códigos sin duplicar.', 'Verificá naturaleza de cuenta antes de asignar a documentos.'],
  }
);

const helpContabilidadNoImputables = hp(
  'Rubros no imputables',
  'Agrupadores de cuentas para reportes y asientos.',
  {
    pasos: ['Usá rubros consistentes con el plan contable interno.'],
  }
);

const helpContabilidadEstrategia = hp(
  'Estrategia contable',
  'Definición de estrategias o esquemas de imputación contable.',
  {
    pasos: ['Revisá parámetros antes de aplicar a nuevos movimientos.', 'Documentá cambios para auditoría.'],
  }
);

const helpContabilidadLibroDiario = hp(
  'Asientos del libro diario',
  'Alta y seguimiento de asientos manuales (LibroDiario + DeLiDi). El total debe debe igualar al total haber. Contabilizar bloquea ediciones; solo SUPERADMIN puede descontabilizar.',
  {
    pasos: [
      'Creá líneas con cuenta imputable, detalle breve e importe en debe o en haber.',
      'Verificá que el asiento esté balanceado antes de guardar.',
      'Usá Contabilizar cuando el asiento esté listo; no podrás editarlo hasta descontabilizar (SUPERADMIN).',
    ],
    errores: ['No se eliminan asientos vinculados a otros módulos ni los ya contabilizados.'],
  }
);

const helpContabilidadLibroMayor = hp(
  'Libro mayor',
  'Consulta de movimientos por cuenta imputable en un rango de fechas: saldo inicial (movimientos anteriores al período, solo en la primera página), líneas paginadas del libro diario y saldo acumulado por línea (debe menos haber). Podés abrir cada asiento desde la columna Ver.',
  {
    pasos: [
      'Seleccioná la cuenta imputable y el período (fechas inclusivas como en libro diario).',
      'Pulsá Consultar para cargar movimientos y saldos.',
      'Libro mayor abre vista previa PDF con todos los movimientos del período; Exportar Excel descarga el mismo detalle en .xlsx.',
      'Usá la paginación inferior para recorrer muchos movimientos en pantalla; el saldo de cada línea respeta el orden cronológico completo.',
      'Usá Ver para revisar el asiento completo en libro diario.',
    ],
    errores: ['Rangos muy amplios pueden superar el límite de líneas del servidor; acotá fechas.'],
  }
);

const helpSistemaModulo = hp(
  'Administración del sistema',
  'Configuración de usuarios del tenant, tablas maestras geográficas, monedas, compañías fiscales, tipos de comprobante y cajas por usuario. Los cambios pueden afectar a todos los módulos.',
  {
    pasos: [
      'Verificá permisos antes de modificar datos críticos.',
      'Documentá cambios en tipos de formulario y compañías para el equipo contable.',
    ],
    errores: ['Operaciones sin permiso serán rechazadas por el servidor.'],
  }
);

const helpConfiguracionesModulo = hp(
  'Configuraciones generales',
  'Transportes, impuestos, ubicaciones, sucursales de venta, tokens de integración y webhooks. Suelen ser tablas base para operaciones comerciales, logística e integraciones.',
  {
    pasos: ['Coordiná con administración antes de eliminar registros referenciados.', 'Probá en ambiente de prueba cambios masivos.'],
  }
);

const helpContabilidadModulo = hp(
  'Contabilidad',
  'Libro diario (asientos manuales), libro mayor por cuenta, plan de cuentas imputables, rubros no imputables y estrategias de imputación. Impacta en cómo se reflejan compras, ventas y tesorería en reportes contables.',
  {
    pasos: ['Mantené coherencia con el plan contable externo del estudio.', 'Evitá eliminar cuentas con movimientos históricos.'],
  }
);

/** Reglas con regex o condiciones especiales (se evalúan primero). */
export const HELP_SPECIAL_RULES: HelpRouteRule[] = [
  {
    id: 'factura-venta-form',
    match: (p) => {
      if (p === ROUTES.ventas.facturas.nuevo || p === ROUTES.ventas.facturas.pos) return true;
      if (p.startsWith(`${ROUTES.ventas.facturas.pos}?`)) return true;
      return /^\/ventas\/facturas\/[^/]+\/(ver|editar)$/.test(p);
    },
    content: helpFacturaVenta,
  },
  {
    id: 'nota-credito-compra-nuevo',
    match: (p) => p === '/compras/notas-credito/nuevo',
    content: helpNotaCreditoCompra,
  },
];

/** Rutas exactas con guía dedicada (sin ambigüedad con prefijos). */
export const HELP_EXACT: Record<string, PageHelpContent> = {
  '/dashboard': helpDashboard,
  '/menu-principal': helpMenuPrincipal,
  '/ventas': helpModuloVentas,
  '/ventas/facturas': helpFacturasListadoVentas,
  '/compras': helpModuloCompras,
  '/inventario': helpModuloInventario,
  '/tesoreria': helpModuloTesoreria,
  '/ventas/consultas-ia': helpConsultaIa('ventas'),
  '/compras/consultas-ia': helpConsultaIa('compras'),
  '/inventario/consultas-ia': helpConsultaIa('inventario'),
  '/tesoreria/consultas-ia': helpConsultaIa('tesorería'),
};

/**
 * Prefijos de mayor a menor longitud al resolver (se ordena en runtime).
 * Cubre formularios, listados y detalles bajo la misma guía temática.
 */
const PREFIX_ENTRIES: [string, PageHelpContent][] = [
  ['/ventas/informes/seguimiento-ventas', helpInformeSeguimientoVentas],
  ['/ventas/informes/cuentas-cobrar', helpInformeCuentasCobrar],
  ['/ventas/informes/resumen-cuenta', helpInformeResumenCuentaVentas],
  ['/ventas/informes/libro-iva-ventas', helpInformeLibroIvaVentas],
  ['/ventas/informes/ventas-dinamico', helpInformeVentasDinamico],
  ['/ventas/informes/ventas-por-producto', helpInformeVentasPorProducto],
  ['/ventas/informes/ventas-resultados', helpInformeVentasResultados],
  ['/ventas/informes/personalizados', helpInformesPersonalizados],
  ['/ventas/informes', helpVentasInformesHub],
  ['/ventas/cotizaciones', helpPedidosCotizaciones('Cotizaciones')],
  ['/ventas/pedidos', helpPedidosCotizaciones('Pedidos')],
  ['/ventas/remitos', helpRemitosVenta],
  ['/ventas/notas-credito', helpNotasCreditoVenta],
  ['/ventas/clientes', helpMaestroCliente],
  ['/ventas/vendedores', helpVentasMaestrosAux],
  ['/ventas/rutas', helpVentasMaestrosAux],
  ['/ventas/visitas', helpVentasMaestrosAux],
  ['/ventas/listaprecios', helpVentasMaestrosAux],
  ['/ventas/lista-precios', helpVentasMaestrosAux],
  ['/ventas/articuloxlistaprecio', helpArticuloListaPrecio],
  ['/compras/informes/compras-dinamico', helpInformeComprasDinamico],
  ['/compras/informes/seguimiento-compras', helpSeguimientoCompras],
  ['/compras/informes/cuentas-pagar', helpComprasInformesHub],
  ['/compras/informes/resumen-cuenta', helpComprasInformesHub],
  ['/compras/informes/libro-iva-compras', helpComprasInformesHub],
  ['/compras/informes/personalizados', helpInformesPersonalizados],
  ['/compras/informes', helpComprasInformesHub],
  ['/compras/facturas', helpComprasFacturas],
  ['/compras/ordenes', helpComprasOrdenes],
  ['/compras/remitos', helpComprasRemitos],
  ['/compras/proveedores', helpProveedores],
  ['/compras/ramosprov', helpRamosProv],
  ['/compras/notas-credito', helpNotasCreditoCompraLista],
  ['/inventario/informes/stock-deposito', helpInventarioInformes],
  ['/inventario/informes/stock-a-fecha', helpInventarioInformes],
  ['/inventario/informes/movimientos-stock', helpInventarioInformes],
  ['/inventario/informes/personalizados', helpInformesPersonalizados],
  ['/inventario/informes', helpInventarioInformes],
  ['/inventario/descuentos', helpDescuentos],
  ['/inventario/productos', helpInventarioProductos],
  ['/inventario/transferencias', helpInventarioTransferencias],
  ['/inventario/ramos', helpInventarioClasificacion],
  ['/inventario/lineas', helpInventarioClasificacion],
  ['/inventario/clases', helpInventarioClasificacion],
  ['/inventario/unidades-medida', helpUnidadesMedida],
  ['/ventas/ordenes-devolucion', helpInventarioOperativo],
  ['/inventario/devoluciones', helpInventarioOperativo],
  ['/inventario/produccion', helpInventarioOperativo],
  ['/produccion', helpInventarioOperativo],
  ['/produccion/recetas', helpInventarioOperativo],
  ['/produccion/ordenes', helpInventarioOperativo],
  ['/inventario/picking', helpInventarioOperativo],
  ['/inventario/conteo', helpInventarioOperativo],
  ['/tesoreria/informes/cheques-terceros-cartera', helpInformeCheques],
  ['/tesoreria/informes/cheques-tercero', helpInformeCheques],
  ['/tesoreria/informes/cheques-propios-pendientes-acreditar', helpInformeCheques],
  ['/tesoreria/informes/cheques-propios', helpInformeCheques],
  ['/tesoreria/informes/movimientos-carteras', helpMovimientosCarterasInforme],
  ['/tesoreria/informes/personalizados', helpInformesPersonalizados],
  ['/tesoreria/informes', helpTesoreriaInformesHub],
  ['/tesoreria/recibos', helpTesoreriaRecibos],
  ['/tesoreria/orden-pago', helpTesoreriaOrdenPago],
  ['/tesoreria/movimientos-varios', helpTesoreriaMovVarios],
  ['/tesoreria/movimientos-varios/nuevo', helpTesoreriaMovVariosNuevo],
  ['/tesoreria/movimientos-bancarios', helpTesoreriaMovBancarios],
  ['/tesoreria/conciliacion-bancaria', helpConciliacionBancaria],
  ['/tesoreria/bancos', helpTablasBancarias],
  ['/tesoreria/carteras', helpTablasBancarias],
  ['/tesoreria/grupos-mcp', helpTablasBancarias],
  ['/tesoreria/medios-mcp', helpTablasBancarias],
  ['/tesoreria/tarjetas', helpTablasBancarias],
  ['/tesoreria/movimientos-caja', helpPlanillaCaja],
  ['/tesoreria/disponibilidades', helpDisponibilidades],
  ['/tesoreria/transferencias', helpTesoreriaTransferencias],
  ['/sistema/usuarios', helpSistemaUsuarios],
  ['/sistema/tipoformulario', helpSistemaTipoFormulario],
  ['/sistema/tipo-formulario', helpSistemaTipoFormulario],
  ['/sistema/provincias', helpSistemaGeografia],
  ['/sistema/localidades', helpSistemaGeografia],
  ['/sistema/monedas', helpSistemaTiposMoneda],
  ['/sistema/monedas/nuevo', helpSistemaTiposMoneda],
  ['/sistema/movmoneda', helpSistemaMonedas],
  ['/sistema/movmoneda/nuevo', helpSistemaMonedas],
  ['/sistema/companias', helpSistemaCompanias],
  ['/sistema/caja-usuario', helpSistemaCajaUsuario],
  ['/sistema/grupo-caja-usuario', helpSistemaCajaUsuario],
  ['/configuraciones/transportes', helpConfigTransportes],
  ['/configuraciones/impuestos', helpConfigImpuestos],
  ['/configuraciones/parametros', helpConfigParametros],
  ['/configuraciones/ubicaciones', helpConfigUbicaciones],
  ['/configuraciones/sucventa', helpConfigSucursales],
  ['/configuraciones/sucursalesventa', helpConfigSucursales],
  ['/configuraciones/tokens-integracion', helpConfigTokens],
  ['/configuraciones/claves-agente', helpConfigClavesAgente],
  ['/configuraciones/webhooks/ayuda', helpConfigWebhooksDocs],
  ['/configuraciones/webhooks', helpConfigWebhooks],
  ['/contabilidad/asientos-diario', helpContabilidadLibroDiario],
  ['/contabilidad/libro-mayor', helpContabilidadLibroMayor],
  ['/contabilidad/plan-cuentas', helpContabilidadModulo],
  ['/contabilidad/informes/personalizados', helpInformesPersonalizados],
  ['/contabilidad/informes', helpContabilidadModulo],
  ['/contabilidad/estado-resultados', helpContabilidadLibroMayor],
  ['/contabilidad/situacion-patrimonial', helpContabilidadLibroMayor],
  ['/contabilidad/imputables', helpContabilidadImputables],
  ['/contabilidad/noimputables', helpContabilidadNoImputables],
  ['/contabilidad/estrategia', helpContabilidadEstrategia],
  ['/sistema', helpSistemaModulo],
  ['/configuraciones', helpConfiguracionesModulo],
  ['/contabilidad', helpContabilidadModulo],
  ['/ventas', helpListadoTrabajoGenerico],
  ['/compras', helpListadoTrabajoGenerico],
  ['/inventario', helpListadoTrabajoGenerico],
  ['/tesoreria', helpListadoTrabajoGenerico],
];

export const HELP_PREFIX_SORTED: [string, PageHelpContent][] = [...PREFIX_ENTRIES].sort(
  (a, b) => b[0].length - a[0].length
);

export function resolveHelpFromCatalog(normalizedPath: string): PageHelpContent | null {
  for (const rule of HELP_SPECIAL_RULES) {
    if (rule.match(normalizedPath)) {
      return rule.content;
    }
  }
  const exact = HELP_EXACT[normalizedPath];
  if (exact) {
    return exact;
  }
  for (const [prefix, content] of HELP_PREFIX_SORTED) {
    if (normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)) {
      return content;
    }
  }
  return null;
}

